import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Menu, LogOut, Bell, LayoutDashboard, ChevronDown, Search, ShoppingCart, Moon, Sun, SlidersHorizontal, Home, LayoutGrid, BadgeCheck, GraduationCap, MessageCircle, Calculator, Sparkles, FlaskConical, Flame, Activity, HeartPulse, Hourglass, HeartHandshake, Brain, ShieldPlus, Package, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import BrandLogo from '@/components/BrandLogo';
import HomeLogoLink from '@/components/HomeLogoLink';
import api from '@/lib/api';
import { tieneDifusion, soloDifusion } from '@/lib/roles';
import { VISIBLE_CATEGORIES } from '@/data/fallbackCatalog';
import { localizeCategories } from '@/i18n/catalog';

// Pestañas con el tratamiento de jadalegal.com: IBM Plex Mono, ~11 px,
// mayúsculas y muy espaciada. Es la fuente que le gustó a Christian.
const navLinkClass = 'font-mono-tech inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-150';

// Iconos + ejemplos por categoría para el menú móvil (estilo Exoma).
const CAT_ICONS = {
  'perdida-peso': Flame, 'hormona-crecimiento': Activity, 'recuperacion': HeartPulse,
  'longevidad': Hourglass, 'sexual-hormonal': HeartHandshake, 'nootropicos': Brain,
  'estetica': Sparkles, 'bioreguladores': ShieldPlus, 'stacks': Layers,
  'suministros': FlaskConical, 'otros': Package,
};
const CAT_EXAMPLES = {
  'perdida-peso': 'Retatrutida, Tirzepatida, Semaglutida', 'recuperacion': 'BPC-157, TB-500, GHK-Cu',
  'hormona-crecimiento': 'CJC-1295, Ipamorelin, Tesamorelina', 'longevidad': 'Epithalon, NAD+, Glutatión',
  'sexual-hormonal': 'PT-141, Kisspeptina, Melanotan II', 'nootropicos': 'Semax, Selank, DSIP',
  'estetica': 'GHK-Cu, Melanotan II', 'bioreguladores': 'Timosina α-1, LL-37',
  'stacks': 'GLOW, KLOW, BPC + TB-500',
  'suministros': 'Agua bacteriostática', 'otros': 'Especialidad',
};
const CAT_TOP = new Set(['perdida-peso', 'recuperacion']);
import { WHATSAPP_URL } from '@/lib/contact';

// Menú "Recursos": herramientas y aprendizaje, en dos columnas.
// Lo de servicio al cliente (contacto, envíos, devoluciones) vive en "Ayuda".
const TOOL_GROUPS = [
  {
    titleKey: 'nav.group.tools',
    items: [
      { to: '/asesor', labelKey: 'nav.advisor', descKey: 'nav.advisor.desc' },
      { to: '/calculadora', labelKey: 'nav.calculator', descKey: 'nav.calculator.desc' },
      { to: '/compuestos', labelKey: 'nav.compounds', descKey: 'nav.compounds.desc' },
      { to: '/aprende', labelKey: 'nav.guides', descKey: 'nav.guides.desc' },
    ],
  },
  {
    titleKey: 'nav.group.info',
    items: [
      { to: '/educacion', labelKey: 'nav.education', descKey: 'nav.education.desc' },
      { to: '/info/calidad', labelKey: 'footer.quality', descKey: 'nav.quality.desc' },
    ],
  },
];

// Menú "Ayuda": contacto, soporte, seguimiento y políticas. Cada entrada es una
// página completa en /info/*, no un enlace suelto a WhatsApp.
const HELP_ITEMS = [
  { to: '/info/contacto', labelKey: 'nav.contact', descKey: 'nav.contact.desc' },
  { to: '/info/soporte', labelKey: 'nav.support', descKey: 'nav.support.desc' },
  { to: '/info/rastreo', labelKey: 'nav.orderStatus', descKey: 'nav.orderStatus.desc' },
  { to: '/aprende/preguntas-frecuentes', labelKey: 'nav.faq', descKey: 'nav.faq.desc' },
  { to: '/info/envios', labelKey: 'footer.shipping', descKey: 'nav.shipping.desc' },
  { to: '/info/devoluciones', labelKey: 'footer.returns', descKey: 'nav.returns.desc' },
];

const Header = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { language, languages, setLanguage, preloadLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  // Un menú por rol (2026-07-30). Toda la información de una persona cuelga de
  // UN solo tablero: el distribuidor del suyo, el cliente de Mi cuenta. Desde
  // aquí se apunta siempre al que le toca, para que no acabe en el otro y tenga
  // que rebotar. (El admin conserva Mi cuenta como cliente que también es.)
  const miPanel = user && user.role === 'distributor' ? '/distribuidor' : '/cuenta';
  const miPanelDeTutoriales = user
    ? `${['distributor', 'admin'].includes(user.role) ? '/distribuidor' : '/cuenta'}?tab=tutoriales`
    : '/tutoriales';

  // Como Resend: la barra nace transparente y fundida con el hero; solo al
  // hacer scroll gana fondo con blur para que el contenido no se le encime.
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const load = () => api.get('/me/notifications')
      .then((r) => { if (alive) setUnread(r.data.unread || 0); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);   // refresca cada minuto
    const clear = () => setUnread(0);      // el usuario ya las vio / las borró
    window.addEventListener('exygen:notifications-seen', clear);
    return () => { alive = false; clearInterval(id); window.removeEventListener('exygen:notifications-seen', clear); };
  }, [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Barra de progreso de lectura (como Exoma): una línea delgada del color
  // primario pegada al borde de abajo del header que avanza de 0 a 100% del
  // ancho conforme se baja en la página. Se anima con transform + rAF sobre un
  // ref (sin estado de React ni reflow por frame) y NUNCA toca overflow del
  // body: el header sigue sticky y siempre visible.
  const progressRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = progressRef.current;
      if (!el) return;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      el.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Las categorias salen del MISMO catalogo con el que el buscador filtra
  // (fallbackCatalog), no del API. El API todavia devuelve los slugs viejos
  // (recuperacion-tejidos, metabolicos, bienestar, accesorios) que NINGUN producto
  // usa: el menu los mostraba y al abrirlos no habia nada. Ademas solo se listan
  // las que de verdad tienen productos, para que no vuelva a pasar.
  useEffect(() => {
    setCategories(VISIBLE_CATEGORIES);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/catalogo?search=${encodeURIComponent(search)}`);
    setMobileOpen(false);
  };

  const currentLang = languages.find((l) => l.code === language);
  // Resolve "system" to the actual applied theme, then toggle to the opposite.
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');

  return (
    <header className="sticky top-0 z-40">
      <div className={`transition-colors duration-200 ${scrolled ? 'bg-background/70 supports-[backdrop-filter]:backdrop-blur-xl' : 'bg-transparent'}`}>
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-[60px] flex items-center gap-2 sm:gap-4 relative">
          {/* Izquierda: logo y el menú móvil. La navegación va aparte, centrada. */}
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 shrink-0">
          {/* Logo pegado a la izquierda */}
          <HomeLogoLink className="shrink-0 order-first origin-left transition-transform duration-200 hover:scale-110" data-testid="header-logo">
            <BrandLogo compact nameOnly />
          </HomeLogoLink>

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="header-mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[22rem] max-w-[92vw] p-0 flex flex-col">
              {/* Arriba de todo: el logo (más chico, para que quepa la fila) y a su
                  derecha un mini menú de 3 líneas con el tema y el idioma
                  (Christian, 2026-07-26). "Mi cuenta" NO va aquí: vive en la
                  cuadrícula de abajo, en el primer lugar, para no duplicarlo. */}
              <SheetHeader className="px-5 pt-5 pb-3">
                <div className="flex items-center justify-between gap-3 pr-8">
                  <SheetTitle asChild>
                    <Link to="/" onClick={() => setMobileOpen(false)}
                      className="flex items-center scale-[0.78] origin-left">
                      <BrandLogo compact />
                    </Link>
                  </SheetTitle>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid="mobile-preferences"
                        aria-label={t('controls.preferences')}
                        className="h-9 w-9 shrink-0 border border-border rounded-lg">
                        <SlidersHorizontal className="h-[17px] w-[17px]" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        {t('controls.appearance')}
                      </DropdownMenuLabel>
                      <DropdownMenuRadioGroup value={isDark ? 'dark' : 'light'} onValueChange={setTheme}>
                        <DropdownMenuRadioItem value="dark" data-testid="mobile-theme-dark">
                          <span className="inline-flex items-center gap-2"><Moon className="h-3.5 w-3.5" /> {t('controls.dark')}</span>
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="light" data-testid="mobile-theme-light">
                          <span className="inline-flex items-center gap-2"><Sun className="h-3.5 w-3.5" /> {t('controls.light')}</span>
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        {t('controls.language')}
                      </DropdownMenuLabel>
                      {/* Al rozar una opción ya se va bajando ese idioma: cuando
                          el dedo suelta, el texto ya está aquí. */}
                      <DropdownMenuRadioGroup value={language} onValueChange={setLanguage}>
                        {languages.map((item) => (
                          <DropdownMenuRadioItem key={item.code} value={item.code} data-testid={`mobile-language-${item.code}`}
                            onPointerEnter={() => preloadLanguage(item.code)}>
                            <span className="inline-flex items-center gap-2"><span aria-hidden="true">{item.flag}</span> {item.label}</span>
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-5 pb-4">
                <form onSubmit={submitSearch} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9 h-11 rounded-xl" placeholder={t('header.searchShort')} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="site-search-input-mobile" />
                </form>

                {/* Los MISMOS accesos que la barra de escritorio (Catálogo, Recursos,
                    Ayuda) más el Asesor, el carrito y Mi cuenta. Recursos y Ayuda son
                    menús en escritorio: aquí bajan a su sección de esta misma hoja.
                    Se quitó "Inicio": el logo de arriba ya lleva a la portada. */}
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {[
                    { to: user ? miPanel : '/login', icon: User, label: t('header.account') },
                    { to: '/catalogo', icon: LayoutGrid, label: t('nav.catalog') },
                    { to: '/asesor', icon: Sparkles, label: t('nav.advisor') },
                    { seccion: 'mob-recursos', icon: FlaskConical, label: t('nav.tools') },
                    { seccion: 'mob-ayuda', icon: MessageCircle, label: t('nav.help') },
                    { to: '/carrito', icon: ShoppingCart, label: t('nav.cart'), badge: count },
                  ].map((it) => {
                    const clase = 'relative flex flex-col items-center gap-1.5 rounded-xl border border-border bg-[hsl(var(--secondary))]/50 py-3 hover:border-[hsl(var(--primary))]/40 transition-colors';
                    const dentro = (
                      <>
                        <it.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                        {it.badge > 0 && (
                          <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[9px] font-bold flex items-center justify-center">{it.badge}</span>
                        )}
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-center leading-tight">{it.label}</span>
                      </>
                    );
                    return it.seccion ? (
                      <button key={it.label} type="button" className={clase}
                        onClick={() => document.getElementById(it.seccion)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                        {dentro}
                      </button>
                    ) : (
                      <Link key={it.label} to={it.to} onClick={() => setMobileOpen(false)} className={clase}>
                        {dentro}
                      </Link>
                    );
                  })}
                </div>

                <Link to="/info/calidad" onClick={() => setMobileOpen(false)} className="mt-3 flex items-center gap-3 rounded-xl border border-border bg-[hsl(var(--secondary))]/50 px-4 py-3 hover:border-[hsl(var(--primary))]/40 transition-colors">
                  <span className="h-9 w-9 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center"><BadgeCheck className="h-4 w-4 text-[hsl(var(--primary))]" /></span>
                  <span className="font-semibold">{t('footer.quality')}</span>
                </Link>

                {/* Péptidos por categoría */}
                <div className="mt-6 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t('nav.byCategory')}</div>
                <div className="grid grid-cols-2 gap-2">
                  {localizeCategories(categories, language).map((c) => {
                    const Icon = CAT_ICONS[c.slug] || Package;
                    return (
                      <Link key={c.slug} to={`/catalogo?category=${c.slug}`} onClick={() => setMobileOpen(false)} className="relative rounded-xl border border-border p-3 hover:border-[hsl(var(--primary))]/40 hover:bg-[hsl(var(--secondary))]/40 transition-colors">
                        {CAT_TOP.has(c.slug) && <span className="absolute top-2 right-2 rounded-full bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] text-[8px] font-bold px-1.5 py-0.5">TOP</span>}
                        <Icon className="h-4 w-4 text-[hsl(var(--primary))] mb-1.5" />
                        <div className="font-semibold text-[13px] leading-tight">{c.name}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{CAT_EXAMPLES[c.slug]}</div>
                      </Link>
                    );
                  })}
                </div>

                {/* Recursos y herramientas */}
                <div id="mob-recursos" className="mt-6 mb-2 scroll-mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t('nav.resources')}</div>
                <div className="flex flex-col gap-1">
                  {[
                    { to: '/asesor', icon: MessageCircle, label: t('nav.advisor') },
                    { to: '/calculadora', icon: Calculator, label: t('nav.calculator') },
                    { to: '/educacion', icon: GraduationCap, label: t('nav.education') },
                    { to: '/info/envios', icon: Package, label: t('footer.shipping') },
                    { to: '/info/devoluciones', icon: ShieldPlus, label: t('footer.returns') },
                  ].map((it) => (
                    <Link key={it.label} to={it.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm hover:bg-[hsl(var(--secondary))]/60 transition-colors">
                      <it.icon className="h-4 w-4 text-muted-foreground" /> {it.label}
                    </Link>
                  ))}
                </div>

                {/* Ayuda: los mismos enlaces que el menú de escritorio. */}
                <div id="mob-ayuda" className="mt-6 mb-2 scroll-mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t('nav.help')}</div>
                <div className="flex flex-col gap-1">
                  {HELP_ITEMS.map((it) => (
                    <Link key={it.labelKey} to={it.to} onClick={() => setMobileOpen(false)} className="rounded-lg px-2 py-2 hover:bg-[hsl(var(--secondary))]/60 transition-colors">
                      <div className="text-sm font-medium">{t(it.labelKey)}</div>
                      <div className="text-[11px] text-muted-foreground">{t(it.descKey)}</div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contactar experto — WhatsApp si hay número; si no, página de contacto. */}
              <div className="border-t border-border p-4">
                {WHATSAPP_URL ? (
                  <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#1fa855] hover:bg-[#188c46] text-white font-semibold py-3.5 transition-colors" data-testid="mobile-whatsapp">
                    <MessageCircle className="h-5 w-5" /> {t('nav.contactExpert')}
                  </a>
                ) : (
                  <Link to="/info/contacto" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 rounded-xl bg-[hsl(var(--primary))] hover:opacity-90 text-[hsl(var(--primary-foreground))] font-semibold py-3.5 transition-colors" data-testid="mobile-contact">
                    <MessageCircle className="h-5 w-5" /> {t('nav.contactExpert')}
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>

          </div>

          {/* Navegación centrada de verdad: va absoluta al centro de la barra, no
              en el hueco entre logo y herramientas (que tienen anchos distintos).
              "Catálogo" lleva un hueco del tamaño del chevron para que el espacio
              entre las tres palabras sea idéntico. */}
          <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-9">
            <Link to="/catalogo" className={navLinkClass} data-testid="nav-catalog">
              {t('nav.catalog')} <span className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger className={navLinkClass} data-testid="nav-tools">
                {t('nav.tools')} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[560px] p-0">
                <div className="grid grid-cols-2 divide-x divide-border">
                  {TOOL_GROUPS.map((group) => (
                    <div key={group.titleKey} className="p-4">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t(group.titleKey)}</div>
                      <ul className="space-y-0.5">
                        {group.items.map((it) => (
                          <li key={it.to}>
                            <button onClick={() => navigate(it.to)} data-testid="nav-tool-link"
                              className="w-full text-left rounded-md px-2 py-1.5 hover:bg-[hsl(var(--muted))] transition-colors">
                              <div className="text-sm font-medium">{t(it.labelKey)}</div>
                              <div className="text-[11px] text-muted-foreground">{t(it.descKey)}</div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={navLinkClass} data-testid="nav-help">
                {t('nav.help')} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 p-2">
                <ul className="space-y-0.5">
                  {HELP_ITEMS.map((it) => (
                    <li key={it.labelKey}>
                      {it.href ? (
                        <a href={it.href} target={it.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" data-testid="nav-help-link"
                          className="block rounded-md px-2 py-1.5 hover:bg-[hsl(var(--muted))] transition-colors">
                          <div className="text-sm font-medium">{t(it.labelKey)}</div>
                          <div className="text-[11px] text-muted-foreground">{t(it.descKey)}</div>
                        </a>
                      ) : (
                        <button onClick={() => navigate(it.to)} data-testid="nav-help-link"
                          className="w-full text-left rounded-md px-2 py-1.5 hover:bg-[hsl(var(--muted))] transition-colors">
                          <div className="text-sm font-medium">{t(it.labelKey)}</div>
                          <div className="text-[11px] text-muted-foreground">{t(it.descKey)}</div>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-0.5 ml-auto shrink-0">
            {/* Preferencias: tema + idioma en un solo menú, igual que jadalegal.com
                (botón de 3 líneas con el código del idioma, secciones y palomita). */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="preferences-menu" aria-label={t('controls.preferences')}
                  className="hidden lg:inline-flex h-9 px-2.5 gap-1.5 border border-border font-mono-tech text-xs uppercase tracking-[0.1em]">
                  <SlidersHorizontal className="h-[15px] w-[15px]" />
                  {currentLang ? currentLang.shortLabel : 'ES'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {t('controls.appearance')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={isDark ? 'dark' : 'light'} onValueChange={setTheme}>
                  <DropdownMenuRadioItem value="dark" data-testid="theme-dark">
                    <span className="inline-flex items-center gap-2"><Moon className="h-3.5 w-3.5" /> {t('controls.dark')}</span>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="light" data-testid="theme-light">
                    <span className="inline-flex items-center gap-2"><Sun className="h-3.5 w-3.5" /> {t('controls.light')}</span>
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {t('controls.language')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={language} onValueChange={setLanguage}>
                  {languages.map((item) => (
                    <DropdownMenuRadioItem key={item.code} value={item.code}
                      onPointerEnter={() => preloadLanguage(item.code)}>
                      <span className="inline-flex items-center gap-2"><span aria-hidden="true">{item.flag}</span> {item.label}</span>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Carrito a la izquierda de la cuenta: el extremo derecho es de
                "Get Started", que es la accion principal de la barra. */}
            <Button variant="ghost" size="icon" className="relative" onClick={() => navigate('/carrito')} data-testid="header-cart-button">
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span data-testid="header-cart-count"
                  className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </Button>

            {/* Profile */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" data-testid="header-account-button" className="relative gap-2 px-2 sm:px-3">
                    <User className="h-5 w-5" />
                    <span className="hidden sm:inline max-w-[120px] truncate text-sm">{(user.name || '').split(' ')[0]}</span>
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--primary))] ring-2 ring-[hsl(var(--background))]" data-testid="header-news-dot" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(miPanel)}><User className="h-4 w-4 mr-2" /> {t('header.account')}</DropdownMenuItem>
                  {/* Difusión (rol propio o extra, p. ej. María) también entra al
                      panel: adentro solo ve sus tres pestañas y el backend le
                      niega el resto. */}
                  {tieneDifusion(user) && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="header-admin-link"><LayoutDashboard className="h-4 w-4 mr-2" /> {soloDifusion(user) ? t('header.difusion') : t('header.admin')}</DropdownMenuItem>
                  )}
                  {['distributor', 'admin'].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate('/distribuidor')} data-testid="header-distributor-link"><LayoutDashboard className="h-4 w-4 mr-2" /> {t('header.distributor')}</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate(user.role === 'admin' ? '/admin?tab=news' : user.role === 'distributor' ? '/distribuidor?tab=news' : '/cuenta?tab=news')} data-testid="header-news-link">
                    <Bell className="h-4 w-4 mr-2" /> {t('news.tab')}
                    {unread > 0 && (
                      <span className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[hsl(var(--primary))] px-1.5 text-[11px] font-semibold text-[hsl(var(--primary-foreground))]" data-testid="header-news-badge">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(miPanelDeTutoriales)} data-testid="header-tutorials-link"><GraduationCap className="h-4 w-4 mr-2" /> {t('header.tutorials')}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="header-logout-button" className="text-destructive hover:bg-destructive hover:text-white focus:bg-destructive focus:text-white"><LogOut className="h-4 w-4 mr-2" /> {t('header.logout')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                {/* Un solo boton: la pagina /login ya trae las dos pestanas
                    (entrar y crear cuenta), asi que dos botones sobraban. */}
                <button onClick={() => navigate('/registro')} data-testid="header-signup-button"
                  className="btn-resend btn-resend-sm hidden sm:inline-flex ml-1">
                  {t('header.getStarted')}
                </button>
                <Button variant="ghost" size="icon" className="sm:hidden" onClick={() => navigate('/login')} data-testid="header-account-button">
                  <User className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Barra de progreso de scroll pegada al borde inferior del header. */}
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[2.5px] pointer-events-none">
        <div ref={progressRef} data-testid="scroll-progress"
          className="h-full w-full origin-left bg-[hsl(var(--primary))]"
          style={{ transform: 'scaleX(0)' }} />
      </div>
    </header>
  );
};

export default Header;
